import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient, type SelectedClient } from '@/core/selectedClient';
import { notesService } from '@/core/services/notes';
import {
  LIST_NOTE_TYPES,
  TEXT_NOTE_LABELS,
  type AdditionalNotesScreen,
  type ListNoteType,
  type NoteDialogData,
  type NoteRow,
  type NoteType,
  type SingletonNoteType,
  type TextNoteType,
} from '@/core/models/notes.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { ClosePopup } from '@/kit/ClosePopup';
import { Icon } from '@/kit/Icon';
import { SectionTabs } from '@/kit/SectionTabs';
import { Toggle } from '@/kit/Toggle';
import { Tooltip } from '@/kit/Tooltip';
import { openDialog, type DialogComponentProps } from '@/kit/dialog';
import { AdvanceCreditDialog, GuaranteesDialog } from './NamedAmountDialog';
import { ControllingPartyDialog } from './ControllingPartyDialog';
import { DirectorEmolumentDialog, NumberofEmployeesDialog, StaffCostsDialog } from './AmountRowsDialog';
import { GiftCardDialog } from './GiftCardDialog';
import {
  DirectorLoanInfoDialog,
  EventInfoDialog,
  ExceptionalItemInfoDialog,
  GiftAidInfoDialog,
  ImpairmentAssetInfoDialog,
  IntangibleAssetInfoDialog,
} from './InfoDialogs';
import {
  AuditInformationDialog,
  BalanceSheetDialog,
  FinancialCommitDialog,
  IndebtednessDialog,
  LimitedByGuaranteeDialog,
} from './NarrativeNoteDialog';
import { useClientAudited } from '@/features/tr-reportsettings/auditStatus';
import { NotesGrid } from './NotesGrid';
import { useClientType } from '@/core/useClientType';
import { ClientKind, isUnincorporated } from '@/features/tr-reportsettings/clientKind';
import { RelatedPartyDialog } from './RelatedPartyDialog';
import './AdditionalNotes.scss';

type NoteLists = Record<ListNoteType, NoteRow[]>;
type NoteDialog = (props: DialogComponentProps<NoteDialogData, NoteRow>) => ReactNode;

function emptyLists(): NoteLists {
  return LIST_NOTE_TYPES.reduce((lists, type) => ({ ...lists, [type]: [] }), {} as NoteLists);
}

const INFO_TOOLTIP =
  'Drag a row by clicking and holding the leftmost part of the desired row, moving it to the desired order.';

/** Report settings > Additional Notes (Angular AdditionalNotesComponent). Every check box and dialog persists at once. */
export function AdditionalNotes() {
  const navigate = useNavigate();
  const [client] = useState<SelectedClient | null>(() => selectedClient());
  const clientType = useClientType(client?.id);
  // Directors' notes, employee numbers and the off-balance-sheet / commitments notes do not apply to a sole trader or partnership.
  const isUnincorporatedClient = isUnincorporated(clientType);
  // An LLP has members, not directors - Directors' Emoluments and Director's Loan read as the members' (BUG-072 round 2).
  const isLlp = clientType === ClientKind.Llp;
  const emolumentsLabel = isLlp ? "Members' Emoluments" : "Directors' Emoluments";
  const directorLoanLabel = isLlp ? 'Members’ Loan' : 'Director’s Loan';
  const addDirectorLoanLabel = isLlp ? 'Add New Members’ Loan' : 'Add New Director’s Loan';
  const [screen, setScreen] = useState<AdditionalNotesScreen | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [lists, setLists] = useState<NoteLists>(emptyLists);
  const [singletons, setSingletons] = useState<Partial<Record<SingletonNoteType, NoteRow>>>({});
  const [enabled, setEnabled] = useState<Partial<Record<SingletonNoteType, boolean>>>({});
  /** BUG-075: audited accounts have an Audit Information note. */
  const audited = useClientAudited(client?.id);

  const apply = (loaded: AdditionalNotesScreen) => {
    setScreen(loaded);
    setLoadError(null);

    const nextLists = emptyLists();
    const nextSingletons: Partial<Record<SingletonNoteType, NoteRow>> = {};
    const nextEnabled: Partial<Record<SingletonNoteType, boolean>> = {};
    for (const note of loaded.notes) {
      if ((LIST_NOTE_TYPES as string[]).includes(note.noteType)) {
        nextLists[note.noteType as ListNoteType].push(note);
      } else {
        nextSingletons[note.noteType as SingletonNoteType] = note;
        nextEnabled[note.noteType as SingletonNoteType] = note.isEnabled;
      }
    }
    setLists(nextLists);
    setSingletons(nextSingletons);
    setEnabled(nextEnabled);
  };

  /** Loads the client's latest accounting period with its notes (no period selector on this screen). */
  const load = () => {
    if (!client) {
      return;
    }
    const periodId = screen?.accountingPeriodId ?? null;
    notesService.getNotes(client.id, periodId).then(
      (loaded) => {
        apply(loaded);
      },
      (error) => {
        setLoadError(error);
        notifyError(error);
      },
    );
  };

  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
    } else {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLocked = !screen || screen.isLocked;

  const requireScreen = (): AdditionalNotesScreen | null => {
    if (!screen) {
      notifyError(loadError ?? 'The additional notes have not loaded yet.');
      return null;
    }
    return screen;
  };

  /** Every notes toggle persists immediately. */
  const toggle = (noteType: SingletonNoteType, checked: boolean) => {
    const current = requireScreen();
    if (!current) {
      return;
    }
    setEnabled((state) => ({ ...state, [noteType]: checked }));
    notesService
      .setNoteEnabled({
        clientId: current.clientId,
        accountingPeriodId: current.accountingPeriodId,
        noteType,
        isEnabled: checked,
      })
      .then(
        (row) => {
          setSingletons((state) => ({ ...state, [noteType]: row }));
          setEnabled((state) => ({ ...state, [noteType]: row.isEnabled }));
        },
        (error) => {
          setEnabled((state) => ({ ...state, [noteType]: !checked }));
          notifyError(error);
        },
      );
  };

  const openNote = (noteType: NoteType, component: NoteDialog, note?: NoteRow, title?: string) => {
    const current = requireScreen();
    if (!current) {
      return;
    }
    const existing = note ?? singletons[noteType as SingletonNoteType] ?? null;
    const data: NoteDialogData = {
      clientId: current.clientId,
      accountingPeriodId: current.accountingPeriodId,
      noteType,
      note: existing,
      priorNotes: current.priorNotes,
      existingNotes: lists[noteType as ListNoteType] ?? [],
      countries: current.countries,
      relatedPartyTypes: current.relatedPartyTypes,
      parentNoteTypes: current.parentNoteTypes ?? [],
      title,
      isLlp,
      clientType,
    };
    openDialog<NoteDialogData, NoteRow>(component as never, { disableClose: true, data })
      .afterClosed()
      .then((saved) => {
        if (saved) {
          notifySuccess(NotificationTextMessage.successMessage);
          load();
        }
      });
  };

  const directorEmolumentDialog = () =>
    openNote('directorEmoluments', DirectorEmolumentDialog, undefined, emolumentsLabel);
  const staffCostsDialog = () => openNote('staffCosts', StaffCostsDialog, undefined, 'Staff Costs');
  const numberofemployeesDialog = () =>
    openNote('averageEmployees', NumberofEmployeesDialog, undefined, 'Average number of employees');
  const balancesheetDialog = () =>
    openNote('offBalanceSheet', BalanceSheetDialog, undefined, 'Off Balance Sheet Arrangements');
  const indentednessDialog = () => openNote('indebtedness', IndebtednessDialog, undefined, 'Indebtedness');
  const auditInformationDialog = () =>
    openNote('auditInformation', AuditInformationDialog, undefined, 'Audit Information');
  const limitedByGuaranteeDialog = () =>
    openNote('limitedByGuarantee', LimitedByGuaranteeDialog, undefined, 'Company Limited by Guarantee');
  const financialDialog = () =>
    openNote(
      'financialCommitments',
      FinancialCommitDialog,
      undefined,
      'Financial Commitments, Guarantees and Contingencies',
    );

  const relatedPartyDialog = (note?: NoteRow) => openNote('relatedParty', RelatedPartyDialog, note);
  const guaranteesDialog = (note?: NoteRow) => openNote('guarantee', GuaranteesDialog, note);
  const advanceCreditDialog = (note?: NoteRow) => openNote('advanceCredit', AdvanceCreditDialog, note);
  const controllingPartyDialog = (note?: NoteRow) => openNote('controllingParty', ControllingPartyDialog, note);

  /** Free-text notes (Gift Aid, Impairments, Intangible Assets, Events, Exceptional Item, Director's Loan, Additional Notes). */
  const addNewGiftAidDialog = (noteType: TextNoteType = 'giftAid', note?: NoteRow) => {
    const label = noteType === 'directorLoan' ? directorLoanLabel : TEXT_NOTE_LABELS[noteType];
    const title = `${note ? 'Edit' : 'Add'} ${label}`;
    openNote(noteType, GiftCardDialog, note, title);
  };

  const deleteNote = (note: NoteRow) => {
    if (!requireScreen()) {
      return;
    }
    openDialog(ClosePopup, { data: { itemName: note.name || 'Note', count: 1 } })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        notesService.deleteNote(note.id).then(
          () => {
            notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
            load();
          },
          (error) => notifyError(error),
        );
      });
  };

  const guaranteeColumns: DataTableColumn<NoteRow>[] = [
    {
      id: 'name',
      header: 'Party Name',
      cell: (element) => (
        <div className="d-flex">
          <div className="onelineclamp">{element.name}</div>
        </div>
      ),
    },
    {
      id: 'relationship',
      header: 'Description',
      cell: (element) => (
        <div className="d-flex">
          <div className="onelineclamp">{element.detail}</div>
        </div>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      headerClassName: 'w-100',
      cell: (element) => (
        <div className="d-flex align-items-center justify-space-between">
          <div className="d-flex align-items-center grid-actions">
            <Button title="Edit" onClick={() => guaranteesDialog(element)}>
              <Icon name="edit" outlined />
            </Button>
            <Button title="Delete" disabled={isLocked} onClick={() => deleteNote(element)}>
              <Icon name="delete_outline" outlined />
            </Button>
          </div>
        </div>
      ),
    },
  ];

  /** Design review 2 Nov 2026: an on/off note is a toggle switch; it saves at once, as the check box did. */
  const noteToggle = (noteType: SingletonNoteType, label: string, className?: string) => (
    <Toggle className={className} checked={!!enabled[noteType]} disabled={isLocked} onCheckedChange={(checked) => toggle(noteType, checked)}>
      {label}
    </Toggle>
  );

  const noteRow = (checkbox: ReactNode, icons: { name: string; onClick: () => void }[]) => (
    <div className="d-flex align-items-center gap-5">
      {checkbox}
      {icons.map((icon) => (
        <Icon key={icon.name} name={icon.name} outlined className="info-icon" onClick={icon.onClick} />
      ))}
    </div>
  );

  /** A free-text block: `info` is the guidance dialog (or the drag hint tooltip for additional notes). */
  /** Design review 2 Nov 2026: a list that loaded empty says so; nothing is said while the notes are still loading. */
  const emptyProps = (what: string, addLabel: string) =>
    screen ? { emptyTitle: `No ${what} added yet`, emptyHint: `Use ${addLabel} above to add the first one.` } : {};

  const textBlock = (type: TextNoteType, heading: string, addLabel: string, info: ReactNode) => (
    <div className="note-box">
      <div className="form-field d-flex align-items-center justify-space-between mb-10">
        <p className="m-0">{heading}</p>
        <div className="d-flex align-items-center gap-10">
          {info}
          <a className="link hyperlink" onClick={() => addNewGiftAidDialog(type)}>
            {addLabel}
          </a>
        </div>
      </div>
      <NotesGrid
        dataSource={lists[type]}
        nameHeader="Title"
        detailHeader="Type"
        {...emptyProps(heading.toLowerCase(), addLabel)}
        disabled={isLocked}
        onEdit={(note) => addNewGiftAidDialog(type, note)}
        onRemove={deleteNote}
      />
    </div>
  );

  const guidance = (open: () => void) => <Icon name="view_list" outlined className="info-icon" onClick={open} />;
  const showInfo = (dialog: () => ReactNode) => () => openDialog(dialog as never, { disableClose: true });

  /** Design review 2 Nov 2026: one tab per section ("tab based, hundred and thousand percent"), the same blocks as before inside each. */
  const notesSection = () => (
    <div className="note-box">
      <div className="form-field">
        <p>Notes</p>
      </div>
      <div className="top-grid">
        {/* ap5-a5: both icons open the Directors' Emoluments note. */}
        {!isUnincorporatedClient &&
          noteRow(noteToggle('directorEmoluments', emolumentsLabel), [
            { name: 'edit', onClick: directorEmolumentDialog },
            { name: 'view_list', onClick: directorEmolumentDialog },
          ])}
        {noteRow(noteToggle('staffCosts', 'Staff Costs'), [{ name: 'view_list', onClick: staffCostsDialog }])}
        {!isUnincorporatedClient &&
          noteRow(noteToggle('averageEmployees', 'Average number of employees'), [
            { name: 'view_list', onClick: numberofemployeesDialog },
          ])}
        {!isUnincorporatedClient &&
          noteRow(noteToggle('offBalanceSheet', 'Off Balance Sheet Arrangements'), [
            { name: 'view_list', onClick: balancesheetDialog },
          ])}
        {!isUnincorporatedClient &&
          noteRow(noteToggle('indebtedness', 'Indebtedness'), [{ name: 'view_list', onClick: indentednessDialog }])}
        {!isUnincorporatedClient &&
          noteRow(noteToggle('financialCommitments', 'Financial Commitments, Guarantees and Contingencies'), [
            { name: 'view_list', onClick: financialDialog },
          ])}
        {audited &&
          noteRow(noteToggle('auditInformation', 'Audit Information'), [
            { name: 'view_list', onClick: auditInformationDialog },
          ])}
        {/* Issue #41: a company limited by guarantee carries this note from the start (the API adds it). */}
        {screen?.limitedByGuarantee &&
          noteRow(noteToggle('limitedByGuarantee', 'Company Limited by Guarantee'), [
            { name: 'view_list', onClick: limitedByGuaranteeDialog },
          ])}
      </div>
    </div>
  );

  const relatedPartySection = () => (
    <div className="note-box">
      <div className="form-field d-flex align-items-center justify-space-between mb-10">
        <p className="m-0">Related Party</p>
        <a className="link hyperlink" onClick={() => relatedPartyDialog()}>
          Add New Party
        </a>
      </div>
      {noteToggle('relatedPartyDisplay', 'Display "Description of the transaction" on report.', 'mb-5')}
      <NotesGrid
        dataSource={lists.relatedParty}
        {...emptyProps('related parties', 'Add New Party')}
        disabled={isLocked}
        onEdit={relatedPartyDialog}
        onRemove={deleteNote}
      />
    </div>
  );

  const guaranteesSection = () => (
    <div className="note-box">
      <div className="form-field d-flex align-items-center justify-space-between mb-10">
        <p className="m-0">Guarantees</p>
        <a className="link hyperlink" onClick={() => guaranteesDialog()}>
          Add New Guarantees
        </a>
      </div>
      {/* task-110: this table duplicates NotesGrid's name/relationship/action columns (to add the extra Edit
          icon) but, unlike every other note card, was never wrapped in .notes-grid - so it kept the old
          auto-sized layout task-109 fixed everywhere else: the Description column sat wherever that
          guarantee's Party Name happened to end, right up against the Action column's icons. */}
      <div className="notes-grid">
        <div className="mat-table-wrapper">
          <DataTable
            stickyHeader
            columns={guaranteeColumns}
            rows={lists.guarantee}
            rowKey={(row) => row.id}
            noDataMessage={
              <>
                No guarantees found.
                <a className="link hyperlink" onClick={() => guaranteesDialog()}>
                  Click Here
                </a>{' '}
                to add new guarantees
              </>
            }
          />
        </div>
      </div>
    </div>
  );

  const controllingPartySection = () => (
    <div className="note-box">
      <div className="form-field d-flex align-items-center justify-space-between mb-10">
        <p className="m-0">Controlling Party</p>
        <a className="link hyperlink" onClick={() => controllingPartyDialog()}>
          Add New Party
        </a>
      </div>
      <NotesGrid
        dataSource={lists.controllingParty}
        nameHeader="Party Name"
        detailHeader="Type"
        {...emptyProps('controlling parties', 'Add New Party')}
        disabled={isLocked}
        onEdit={controllingPartyDialog}
        onRemove={deleteNote}
      />
    </div>
  );

  const advanceCreditSection = () => (
    <div className="note-box">
      <div className="form-field d-flex align-items-center justify-space-between mb-10">
        <p className="m-0">Advances &amp; Credits</p>
        <a className="link hyperlink" onClick={() => advanceCreditDialog()}>
          Add New Advances &amp; Credits
        </a>
      </div>
      <NotesGrid
        dataSource={lists.advanceCredit}
        nameHeader="Name"
        detailHeader="Description"
        {...emptyProps('advances or credits', 'Add New Advances & Credits')}
        disabled={isLocked}
        onEdit={advanceCreditDialog}
        onRemove={deleteNote}
      />
    </div>
  );

  /** The tabs, in the page's old order; each one's body is the block that used to be stacked on the page. */
  const sections: { id: string; label: string; count?: number; render: () => ReactNode }[] = [
    { id: 'notes', label: 'Notes', render: notesSection },
    { id: 'relatedParty', label: 'Related Party', count: lists.relatedParty.length, render: relatedPartySection },
    { id: 'guarantee', label: 'Guarantees', count: lists.guarantee.length, render: guaranteesSection },
    { id: 'controllingParty', label: 'Controlling Party', count: lists.controllingParty.length, render: controllingPartySection },
    { id: 'advanceCredit', label: 'Advances & Credits', count: lists.advanceCredit.length, render: advanceCreditSection },
    {
      id: 'giftAid',
      label: 'Gift Aid',
      count: lists.giftAid.length,
      render: () => textBlock('giftAid', 'Gift Aid', 'Add New Gift Aid', guidance(showInfo(GiftAidInfoDialog))),
    },
    {
      id: 'impairment',
      label: 'Impairments of Assets',
      count: lists.impairment.length,
      render: () =>
        textBlock('impairment', 'Impairments of Assets', 'Add New Impairments of Assets', guidance(showInfo(ImpairmentAssetInfoDialog))),
    },
    {
      id: 'intangibleAsset',
      label: 'Intangible Assets',
      count: lists.intangibleAsset.length,
      render: () =>
        textBlock('intangibleAsset', 'Intangible Assets', 'Add New Intangible Assets', guidance(showInfo(IntangibleAssetInfoDialog))),
    },
    {
      id: 'event',
      label: 'Events after Balance Sheet Date',
      count: lists.event.length,
      render: () => textBlock('event', 'Events after Balance Sheet Date', 'Add New Events', guidance(showInfo(EventInfoDialog))),
    },
    {
      id: 'exceptionalItem',
      label: 'Exceptional Item',
      count: lists.exceptionalItem.length,
      render: () => textBlock('exceptionalItem', 'Exceptional Item', 'Add New Item', guidance(showInfo(ExceptionalItemInfoDialog))),
    },
    ...(isUnincorporatedClient
      ? []
      : [
          {
            id: 'directorLoan',
            label: directorLoanLabel,
            count: lists.directorLoan.length,
            render: () =>
              textBlock('directorLoan', directorLoanLabel, addDirectorLoanLabel, guidance(showInfo(DirectorLoanInfoDialog))),
          },
        ]),
    {
      id: 'additionalNote',
      label: 'Additional/Extension Notes',
      count: lists.additionalNote.length,
      render: () =>
        textBlock(
          'additionalNote',
          'Additional/Extension Notes',
          'Add New Notes',
          <Tooltip title={INFO_TOOLTIP}>
            <Icon name="info" outlined className="info-icon" />
          </Tooltip>,
        ),
    },
  ];

  return (
    <SideMenuPage menu="settings" syncContent>
      <div className="sidenav-details additional-notes">
        <div className="sidenav-header">
          <div className="d-flex align-items-center justify-space-between">
            <h2>Additional Notes</h2>
            <Button className="close-btn" onClick={() => navigate(-1)}>
              <Icon name="close" />
            </Button>
          </div>
        </div>
        <div className="sidenav-body pt-1">
          <SectionTabs
            ariaLabel="Additional Notes sections"
            tabs={sections.map((section) => ({ id: section.id, label: section.label, badge: section.count || undefined }))}
          >
            {(active) => sections.find((section) => section.id === active)?.render()}
          </SectionTabs>
        </div>
      </div>
    </SideMenuPage>
  );
}
